import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import marketplaceService from '../../../services/marketplaceService';

const ActiveTransactions = ({ onRefresh }) => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    setLoading(true);
    const { data, error } = await marketplaceService?.getUserTransactions(user?.id);
    if (!error) {
      const activeTransactions = data?.filter(t => t?.status === 'held' || t?.status === 'pending') || [];
      setTransactions(activeTransactions);
    }
    setLoading(false);
  };

  const handleConfirmTransaction = async (transactionId, userRole) => {
    if (!confirm('Confirm that you have received/sent the item?')) return;
    
    const { error } = await marketplaceService?.confirmTransaction(transactionId, userRole);
    if (!error) {
      loadTransactions();
      onRefresh?.();
    }
  };

  const getUserRole = (transaction) => {
    return transaction?.buyer_id === user?.id ? 'buyer' : 'seller';
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      held: 'bg-blue-100 text-blue-800',
      released: 'bg-green-100 text-green-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles?.[status] || styles?.pending}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="bg-card border border-border rounded-lg h-40" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {transactions?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="DollarSign" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No active transactions</h3>
          <p className="text-muted-foreground">Your escrow-protected transactions will appear here</p>
        </div>
      ) : (
        transactions?.map(transaction => {
          const userRole = getUserRole(transaction);
          const isBuyer = userRole === 'buyer';
          const otherParty = isBuyer ? transaction?.seller : transaction?.buyer;
          const userConfirmed = isBuyer ? transaction?.buyer_confirmed : transaction?.seller_confirmed;
          const otherConfirmed = isBuyer ? transaction?.seller_confirmed : transaction?.buyer_confirmed;

          return (
            <div key={transaction?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">
                      {transaction?.listing?.title}
                    </h3>
                    {getStatusBadge(transaction?.status)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {isBuyer ? 'Buying from' : 'Selling to'}: {otherParty?.full_name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">
                    ${parseFloat(transaction?.amount || 0)?.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {transaction?.currency || 'USD'}
                  </p>
                </div>
              </div>
              {/* Escrow Status */}
              <div className="bg-muted/20 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-foreground">Escrow Protection</h4>
                  <Icon name="Shield" size={16} className="text-green-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={userConfirmed ? "CheckCircle" : "Circle"} 
                      size={16} 
                      className={userConfirmed ? "text-green-500" : "text-muted-foreground"}
                    />
                    <span className="text-sm text-foreground">
                      {isBuyer ? 'You confirmed receipt' : 'You confirmed shipment'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={otherConfirmed ? "CheckCircle" : "Circle"} 
                      size={16} 
                      className={otherConfirmed ? "text-green-500" : "text-muted-foreground"}
                    />
                    <span className="text-sm text-foreground">
                      {isBuyer ? 'Seller confirmed shipment' : 'Buyer confirmed receipt'}
                    </span>
                  </div>
                </div>
              </div>
              {/* Transaction Details */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Payment Status</p>
                  <p className="text-sm font-medium text-foreground capitalize">
                    {transaction?.payment_status?.replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Platform Fee</p>
                  <p className="text-sm font-medium text-foreground">
                    ${parseFloat(transaction?.platform_fee || 0)?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Created</p>
                  <p className="text-sm font-medium text-foreground">
                    {new Date(transaction?.created_at)?.toLocaleDateString()}
                  </p>
                </div>
              </div>
              {/* Action Button */}
              {!userConfirmed && transaction?.status === 'held' && (
                <div className="pt-4 border-t border-border">
                  <Button
                    onClick={() => handleConfirmTransaction(transaction?.id, userRole)}
                    iconName="CheckCircle"
                    iconPosition="left"
                    fullWidth
                  >
                    {isBuyer ? 'Confirm Item Received' : 'Confirm Item Shipped'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Funds will be released when both parties confirm
                  </p>
                </div>
              )}
              {userConfirmed && !otherConfirmed && (
                <div className="pt-4 border-t border-border">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-900">
                      ✓ You've confirmed. Waiting for {isBuyer ? 'seller' : 'buyer'} confirmation...
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default ActiveTransactions;
